import { lazy } from 'react';
import {
  createBrowserRouter,
  Outlet,
  Route,
  Routes,
  useRouteError,
} from 'react-router-dom';

import useSuspense from 'app/hooks/UseSuspense';
import { useReleaseSwitch } from 'app/platform/release/release';
import { PUBLIC_ROUTES_CONFIG } from 'routes/PublicRouteConfig';
import { PROTECTED_ROUTES_CONFIG } from 'routes/ProtectedRouteConfig';
import { ProtectedShell } from 'ProtectedShell';
import { PublicShell } from 'PublicShell';

const NotFoundPage = lazy(() => import('app/pages/Errors/NotFound'));

// Inside the boundary, so it waits for a lazy route to commit; until then the hidden left page keeps its unload guard.
const ReleaseSwitch = () => {
  useReleaseSwitch();
  return null;
};

function AppRoutes() {
  return useSuspense(
    <>
      <ReleaseSwitch />
      <Routes>
        <Route element={<ProtectedShell>{<Outlet />}</ProtectedShell>}>
          {PROTECTED_ROUTES_CONFIG.map((route, index) => (
            <Route key={index} path={route.path} element={route.element} />
          ))}
        </Route>
        <Route element={<PublicShell>{<Outlet />}</PublicShell>}>
          {PUBLIC_ROUTES_CONFIG.map((route, index) => (
            <Route key={index} path={route.path} element={route.element} />
          ))}
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
}

// The data router catches every render error in its own boundary; rethrow so
// they reach the app ErrorBoundary (logging, branded fallback, chunk reload).
function RethrowRouteError(): never {
  throw useRouteError();
}

// No loaders: routes stay in descendant <Routes>, so navigation is synchronous.
// Own module so Fast Refresh of App.tsx doesn't build a second router.
export const router = createBrowserRouter(
  [{ path: '*', element: <AppRoutes />, errorElement: <RethrowRouteError /> }],
  {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  }
);

import.meta.hot?.dispose(() => router.dispose());
